
const libPictView = require('pict-view');

/**
 * Project Clone workflow view.
 *
 * Three-step accordion (Prepare → Ready → Results). All state lives in
 * pict.AppData.ProjectClone and all behavior lives in ProjectCloneProvider; the
 * view only triggers derivation + a one-time project load and repaints
 * declaratively from AppData.
 */
class ProjectClone extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.ProjectCloneProvider;
	}

	deriveRenderState()
	{
		this.provider.deriveRenderState();
	}

	onBeforeRender(pRenderable)
	{
		this.deriveRenderState();
		return super.onBeforeRender(pRenderable);
	}

	onBeforeRenderAsync(fCallback)
	{
		this.deriveRenderState();
		return fCallback();
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		// Wire the Select2-backed Project pickers each render (the destination
		// picker only when in existing-project mode and the field is visible).
		this._initProjectSelect('#ProjectClone-SourceSelect', 'setSource', false);
		const tmpState = this.pict.AppData.ProjectClone;
		if (tmpState && tmpState.DestMode === 'existing')
		{
			this._initProjectSelect('#ProjectClone-DestSelect', 'setDest', true);
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/**
	 * Initialize a Select2-powered Project picker (AJAX, session-scoped, searchable)
	 * on the given select element and route its selection back into the provider.
	 *
	 * @param {string} pSelector - CSS selector for the <select> element.
	 * @param {string} pMethod - Provider method to call on change ('setSource' | 'setDest').
	 * @param {boolean} pExcludeSource - When true, filter the chosen source project out of results.
	 */
	_initProjectSelect(pSelector, pMethod, pExcludeSource)
	{
		const tmpJQuery = window.jQuery;
		if (!tmpJQuery || !tmpJQuery.fn || !tmpJQuery.fn.select2)
		{
			return;
		}
		const tmpElement = tmpJQuery(pSelector);
		if (tmpElement.length === 0 || tmpElement.data('select2'))
		{
			// Not in the DOM yet, or already initialized on this element.
			return;
		}
		const tmpProvider = this.provider;
		const tmpOptions = this.pict.providers.Select2Util.getOptionsAjax('Project',
			{
				SearchFallbackPlaceholder: 'Search projects by name...',
				HandleSearchFilters: (pFilters) =>
				{
					// Exclude the chosen source project from the destination results.
					if (pExcludeSource)
					{
						const tmpSourceID = this.pict.AppData.ProjectClone && this.pict.AppData.ProjectClone.SourceIDProject;
						if (tmpSourceID)
						{
							pFilters.push(`FBV~IDProject~NE~${parseInt(tmpSourceID, 10)}`);
						}
					}
					// Always sort results by Name ascending (mirrors the platform
					// ProjectSelector input).
					if (!pFilters.some((pFilter) => String(pFilter).indexOf('FSF') === 0))
					{
						pFilters.push('FSF~Name~ASC~0');
					}
					return pFilters;
				},
			});
		tmpElement.select2(tmpOptions);
		tmpElement.on('select2:select', (pEvent) =>
		{
			const tmpData = (pEvent.params && pEvent.params.data) || {};
			// Defer so Select2 finishes its own event cycle before the view re-renders
			// (the re-render replaces this element).
			window.setTimeout(() => tmpProvider[pMethod](String(tmpData.id), tmpData.text), 0);
		});
		tmpElement.on('select2:clear', () =>
		{
			window.setTimeout(() => tmpProvider[pMethod](''), 0);
		});
	}
}

module.exports = ProjectClone;

module.exports.default_configuration =
{
	ViewIdentifier: 'ProjectClone',
	DefaultRenderable: 'ProjectCloneWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.project-clone-entity-row td { vertical-align: middle; }
		.project-clone-spinner { display: inline-block; width: 1.5rem; height: 1.5rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #485fc7); border-radius: 50%; animation: project-clone-spin 0.8s linear infinite; vertical-align: middle; }
		@keyframes project-clone-spin { to { transform: rotate(360deg); } }
	`,
	Templates:
	[
		{
			Hash: 'ProjectClone-SelectedOption',
			Template: /*html*/`<option value="{~D:Record.IDProject~}" selected>{~D:Record.Name~}</option>`,
		},
		{
			Hash: 'ProjectClone-EntityRow',
			Template: /*html*/`
				<tr class="project-clone-entity-row {~D:Record.RowClass~}">
					<td>
						<label class="checkbox">
							<input type="checkbox" {~D:Record.CheckedAttr~}
								onchange="_Pict.providers.ProjectCloneProvider.toggleEntity('{~D:Record.Entity~}')" />
							&nbsp;<strong>{~D:Record.Label~}</strong>
						</label>
					</td>
					<td class="has-text-right">{~D:Record.Count~}</td>
				</tr>`,
		},
		{
			Hash: 'ProjectClone-WarningRow',
			Template: /*html*/`<li>{~D:Record.Message~}</li>`,
		},
		{
			Hash: 'ProjectClone-GenerateError',
			Template: /*html*/`
				<div class="notification is-danger mt-3">
					<span class="icon"><i class="fas fa-triangle-exclamation"></i></span>
					&nbsp;Could not generate the clone preview: {~D:Record.Message~}
				</div>`,
		},
		{
			Hash: 'ProjectClone-ResultBanner',
			Template: /*html*/`
				<div class="notification {~D:Record.TypeClass~} mt-2">
					<h4 class="title is-4">
						<span class="icon"><i class="fas {~D:Record.Icon~}"></i></span>
						&nbsp;{~D:Record.Title~}
					</h4>
					<p>{~D:Record.Detail~}</p>
				</div>`,
		},
		{
			Hash: 'ProjectCloneWrap',
			Template: /*html*/`
			<section class="section">
				<div class="container">
					<div class="mb-5">
						<a href="#/Settings" class="button is-small mb-3">
							<span class="icon"><i class="fas fa-arrow-left"></i></span>
							<span>Back to Configuration</span>
						</a>
						<h1 class="title is-3">
							<span class="icon"><i class="fas fa-clone"></i></span>&nbsp;Clone Project
						</h1>
						<p class="subtitle is-5 has-text-grey">
							Copy configuration, line items, sample plans and more from one project to another
						</p>
					</div>

					<!-- Step 1: Prepare for Cloning -->
					<article class="message is-info mb-4" id="ProjectClone-Step1">
						<div class="message-header" style="cursor:pointer"
							onclick="_Pict.providers.ProjectCloneProvider.goToStep(1)">
							<p><span class="icon"><i class="fas fa-sliders"></i></span>&nbsp;Step 1: Prepare for Cloning</p>
							<span class="tag is-soft {~D:AppData.ProjectClone.Step1_StatusClass~}">{~D:AppData.ProjectClone.Step1_StatusLabel~}</span>
						</div>
						<div class="message-body {~D:AppData.ProjectClone.Step1_BodyClass~}">
							<div class="field">
								<label class="label">Source Project</label>
								<div class="control">
									<select id="ProjectClone-SourceSelect" style="width:100%">
										<option value=""></option>
										{~TS:ProjectClone-SelectedOption:AppData.ProjectClone.SourceSelectedSlot~}
									</select>
								</div>
								<p class="help">Search and select the project to copy configuration and data from.</p>
							</div>

							<div class="field">
								<label class="label">Destination Project</label>
								<div class="control">
									<label class="radio">
										<input type="radio" name="ProjectClone-DestMode" {~D:AppData.ProjectClone.DestModeNewChecked~}
											onchange="_Pict.providers.ProjectCloneProvider.setDestMode('new')" />
										Create a new project
									</label>
									&nbsp;&nbsp;
									<label class="radio">
										<input type="radio" name="ProjectClone-DestMode" {~D:AppData.ProjectClone.DestModeExistingChecked~}
											onchange="_Pict.providers.ProjectCloneProvider.setDestMode('existing')" />
										Clone into an existing project
									</label>
								</div>
							</div>

							<div class="field {~D:AppData.ProjectClone.DestSelectorClass~}">
								<label class="label">Existing Destination Project</label>
								<div class="control">
									<select id="ProjectClone-DestSelect" style="width:100%">
										<option value=""></option>
										{~TS:ProjectClone-SelectedOption:AppData.ProjectClone.DestSelectedSlot~}
									</select>
								</div>
								<p class="help">Search and select an existing project to clone into. Must differ from the source.</p>
							</div>

							<div class="field {~D:AppData.ProjectClone.NewProjectFieldsClass~}">
								<label class="label">Destination Project Name</label>
								<div class="control">
									<input class="input" type="text"
										placeholder="{~D:AppData.ProjectClone.NamePlaceholder~}"
										value="{~D:AppData.ProjectClone.DestName~}"
										onfocus="this.select()"
										oninput="_Pict.providers.ProjectCloneProvider.setName(this.value)" />
								</div>
								<p class="help">Used when creating a new project. Click to select all and type to replace.</p>
							</div>

							<div class="field {~D:AppData.ProjectClone.NewProjectFieldsClass~}">
								<label class="label">Destination Project Description</label>
								<div class="control">
									<textarea class="textarea" rows="3"
										placeholder="Optional description for the destination project"
										oninput="_Pict.providers.ProjectCloneProvider.setDescription(this.value)">{~D:AppData.ProjectClone.DestDescription~}</textarea>
								</div>
							</div>

							{~TS:ProjectClone-GenerateError:AppData.ProjectClone.GenerateErrorSlot~}

							<div class="field is-grouped is-grouped-right mt-4">
								<div class="control">
									<button class="button is-link {~D:AppData.ProjectClone.GenerateButtonClass~}"
										{~D:AppData.ProjectClone.GenerateDisabledAttr~}
										onclick="_Pict.providers.ProjectCloneProvider.generateComprehension()">
										<span class="icon"><i class="fas fa-gears"></i></span>
										<span>Generate Clone Preview</span>
									</button>
								</div>
							</div>
						</div>
					</article>

					<!-- Step 2: Ready for Cloning -->
					<article class="message is-info mb-4" id="ProjectClone-Step2">
						<div class="message-header" style="cursor:pointer"
							onclick="_Pict.providers.ProjectCloneProvider.goToStep(2)">
							<p><span class="icon"><i class="fas fa-list-check"></i></span>&nbsp;Step 2: Ready for Cloning</p>
							<span class="tag is-soft {~D:AppData.ProjectClone.Step2_StatusClass~}">{~D:AppData.ProjectClone.Step2_StatusLabel~}</span>
						</div>
						<div class="message-body {~D:AppData.ProjectClone.Step2_BodyClass~}">
							<p class="mb-3 has-text-grey">{~D:AppData.ProjectClone.SummaryLabel~}</p>
							<p class="mb-3">Choose which entities to include in the clone. Counts are the number of records that would be created.</p>

							<div class="table-container">
								<table class="table is-fullwidth is-hoverable is-striped">
									<thead>
										<tr>
											<th>Entity</th>
											<th class="has-text-right">Records</th>
										</tr>
									</thead>
									<tbody>
										{~TS:ProjectClone-EntityRow:AppData.ProjectClone.EntityRows~}
									</tbody>
								</table>
							</div>

							<!-- Refresh-preview affordance. Visible only when the user has
							     opted into a heavy entity that the last build skipped — the
							     comprehension currently has no data for it, so Proceed is
							     gated until we re-run the build. Clicking Refresh re-issues
							     buildComprehension with the current IncludeMap (and caps),
							     which fetches the newly-included entities and populates counts. -->
							<div class="notification is-warning is-soft mt-3 {~D:AppData.ProjectClone.RefreshHiddenClass~}">
								<div class="level is-mobile">
									<div class="level-left">
										<div class="level-item">
											<span class="icon"><i class="fas fa-rotate"></i></span>
											<span>&nbsp;You've opted into entities that weren't in the last preview. Refresh to fetch them before proceeding.</span>
										</div>
									</div>
									<div class="level-right">
										<div class="level-item">
											<button class="button is-warning is-small"
												{~D:AppData.ProjectClone.RefreshDisabledAttr~}
												onclick="_Pict.providers.ProjectCloneProvider.refreshPreview()">
												<span class="icon"><i class="fas fa-rotate"></i></span>
												<span>Refresh preview</span>
											</button>
										</div>
									</div>
								</div>
							</div>

							<!-- Per-entity record caps for heavy classes. Independent of the
							     Contract toggle below; these bound how many Observation / Document
							     rows the source-side preview / clone pulls. Defaults match the
							     adapter recipe defaults (5000 each); 0 lifts the cap entirely. -->
							<div class="mt-4">
								<p class="has-text-weight-semibold is-size-7 has-text-grey mb-2">Heavy entity caps</p>
								<div class="field is-horizontal">
									<div class="field-label is-normal">
										<label class="label is-size-7 has-text-grey">Max Observation rows</label>
									</div>
									<div class="field-body">
										<div class="field is-narrow">
											<div class="control">
												<input class="input is-small" type="number" min="0" step="100"
													value="{~D:AppData.ProjectClone.MaxRecordsObservation~}"
													onchange="_Pict.providers.ProjectCloneProvider.updateMaxRecords('Observation', this.value)" />
											</div>
											<p class="help">Cap on source Observation rows pulled into the clone. 0 = no cap. Uncheck the Observation row above to skip entirely.</p>
										</div>
									</div>
								</div>
								<div class="field is-horizontal">
									<div class="field-label is-normal">
										<label class="label is-size-7 has-text-grey">Max Document rows</label>
									</div>
									<div class="field-body">
										<div class="field is-narrow">
											<div class="control">
												<input class="input is-small" type="number" min="0" step="100"
													value="{~D:AppData.ProjectClone.MaxRecordsDocument~}"
													onchange="_Pict.providers.ProjectCloneProvider.updateMaxRecords('Document', this.value)" />
											</div>
											<p class="help">Cap on source Document rows pulled into the clone. 0 = no cap. Uncheck the Document row above to skip entirely.</p>
										</div>
									</div>
								</div>
							</div>

							<hr class="my-3" />

							<div class="field">
								<label class="checkbox">
									<input type="checkbox"
										{~D:AppData.ProjectClone.ContractCheckedAttr~}
										{~D:AppData.ProjectClone.ContractDisabledAttr~}
										onchange="_Pict.providers.ProjectCloneProvider.toggleContract()" />
									&nbsp;Also clone the source project's Contract
								</label>
								<p class="help">
									Creates a new linked contract for a new (or contract-less) destination, or merges the
									source contract into the destination's existing one. Available when the source project has a contract.
									<span class="{~D:AppData.ProjectClone.RebuildingClass~}"><em>&nbsp;Updating preview…</em></span>
								</p>
							</div>

							<div class="content mt-3">
								<p class="has-text-grey-light is-size-7" id="ProjectClone-WarningsHeader">Notes from the preview:</p>
								<ul class="is-size-7 has-text-grey">
									{~TS:ProjectClone-WarningRow:AppData.ProjectClone.WarningRows~}
								</ul>
							</div>

							<hr />
							<div class="field is-grouped is-grouped-right">
								<div class="control">
									<button class="button" onclick="_Pict.providers.ProjectCloneProvider.backToPrepare()">
										<span class="icon"><i class="fas fa-arrow-left"></i></span>
										<span>Back</span>
									</button>
								</div>
								<div class="control">
									<button class="button is-link" {~D:AppData.ProjectClone.ProceedDisabledAttr~}
										onclick="_Pict.providers.ProjectCloneProvider.proceedWithClone()">
										<span class="icon"><i class="fas fa-cloud-arrow-up"></i></span>
										<span>Proceed with Project Clone</span>
									</button>
								</div>
							</div>
						</div>
					</article>

					<!-- Step 3: Cloning Results -->
					<article class="message is-info mb-4" id="ProjectClone-Step3">
						<div class="message-header" style="cursor:pointer"
							onclick="_Pict.providers.ProjectCloneProvider.goToStep(3)">
							<p><span class="icon"><i class="fas fa-flag-checkered"></i></span>&nbsp;Step 3: Cloning Results</p>
							<span class="tag is-soft {~D:AppData.ProjectClone.Step3_StatusClass~}">{~D:AppData.ProjectClone.Step3_StatusLabel~}</span>
						</div>
						<div class="message-body {~D:AppData.ProjectClone.Step3_BodyClass~}">
							<!-- In-progress experience -->
							<div class="has-text-centered py-5 {~D:AppData.ProjectClone.CloneState.PanelClass~}" id="ProjectClone-Progress">
								<span class="project-clone-spinner"></span>
								<p class="mt-3 has-text-weight-semibold">Cloning in progress…</p>
								<p class="has-text-grey">{~D:AppData.ProjectClone.CloneState.StatusText~}</p>
								<progress class="progress is-info mt-3" max="100">working…</progress>
								<p class="has-text-grey-light is-size-7">Please do not navigate away or close this tab until the clone finishes — it can take more than 30 seconds.</p>
							</div>

							<!-- Result banner (success or failure) -->
							{~TS:ProjectClone-ResultBanner:AppData.ProjectClone.ResultSlot~}

							<ul class="is-size-7 has-text-grey">
								{~TS:ProjectClone-WarningRow:AppData.ProjectClone.ResultWarningRows~}
							</ul>

							<hr />
							<div class="field is-grouped is-grouped-right">
								<div class="control">
									<button class="button is-link" {~D:AppData.ProjectClone.DoneDisabledAttr~}
										onclick="_Pict.providers.ProjectCloneProvider.finishAndLeave()">
										<span class="icon"><i class="fas fa-check"></i></span>
										<span>Done</span>
									</button>
								</div>
							</div>
						</div>
					</article>
				</div>
			</section>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'ProjectCloneWrap',
			TemplateHash: 'ProjectCloneWrap',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
		},
	],
};
